import type {ScriptDiagnostic} from "./scriptChecker";

export class ScriptValidationError extends Error {
  readonly code = "script_validation";
  readonly detail: string;

  constructor(diagnostics: ScriptDiagnostic[]) {
    super(
      "The generated script did not pass Excel API validation. No script changes were applied.",
    );
    this.detail =
      "Script validation failed before execution. Correct the following errors using documented Office.js APIs. Do not suppress checks, use any types, or execute dynamically generated code. Return JavaScript, not TypeScript.\n" +
      diagnostics
        .map(
          ({line, column, message}) =>
            `Line ${line}, column ${column}: ${message}`,
        )
        .join("\n");
  }
}

export class ScriptCheckerUnavailableError extends Error {
  readonly code = "script_checker_unavailable";

  constructor() {
    super(
      "Excel script validation is unavailable. The script was not run. Please try again.",
    );
  }
}

interface PendingCheck {
  resolve: (diagnostics: ScriptDiagnostic[]) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

let worker: Worker | undefined;
let nextId = 0;
const pending = new Map<number, PendingCheck>();

function resetWorker(): void {
  worker?.terminate();
  worker = undefined;
  for (const check of pending.values()) {
    clearTimeout(check.timer);
    check.reject(new ScriptCheckerUnavailableError());
  }
  pending.clear();
}

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL("./scriptValidation.worker.ts", import.meta.url),
    );
    worker.onmessage = (
      event: MessageEvent<{
        id: number;
        diagnostics?: ScriptDiagnostic[];
        error?: boolean;
      }>,
    ) => {
      const check = pending.get(event.data.id);
      if (!check) return;
      if (event.data.error || !Array.isArray(event.data.diagnostics)) {
        resetWorker();
        return;
      }
      clearTimeout(check.timer);
      pending.delete(event.data.id);
      check.resolve(event.data.diagnostics);
    };
    worker.onerror = resetWorker;
    worker.onmessageerror = resetWorker;
  }
  return worker;
}

export async function validateScript(code: string): Promise<void> {
  let activeWorker: Worker;
  try {
    activeWorker = getWorker();
  } catch {
    resetWorker();
    throw new ScriptCheckerUnavailableError();
  }
  const diagnostics = await new Promise<ScriptDiagnostic[]>(
    (resolve, reject) => {
      const id = ++nextId;
      pending.set(id, {
        resolve,
        reject,
        timer: setTimeout(resetWorker, 30_000),
      });
      try {
        activeWorker.postMessage({id, code});
      } catch {
        resetWorker();
      }
    },
  );
  if (diagnostics.length > 0) throw new ScriptValidationError(diagnostics);
}
